import { gql } from '@apollo/client';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useCreateCartProduct from '../../../../../../hooks/useCreateCartProduct';
import useMedia from '../../../../../../hooks/useMedia';
import {
  formatSegmentDataForProductAddedEvent,
  segmentTrackEvent
} from '@/utils/segmentTracking';
import { isPreOrder } from '@/models/product';

const Wrapper = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.buttons.primary.backgroundColor};
  border: none;
  border-radius: ${({ theme }) => theme.buttons.primary.borderRadius};
  color: ${({ theme }) => theme.buttons.primary.color};
  display: flex;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.buttons.primary.fontSize};
  font-style: normal;
  font-weight: ${({ theme }) => theme.buttons.primary.fontWeight};
  height: 50px;
  justify-content: center;
  letter-spacing: 0.04em;
  line-height: 23px;
  margin-right: ${({ isWallpaper }) => (isWallpaper ? '0' : '24px')};
  opacity: ${({ disabled }) => (disabled ? 0.7 : 0.9)};
  width: ${({ isWallpaper }) => (isWallpaper ? '100%' : '250px')};

  &:hover {
    transition: all 0.3s;
    opacity: 0.8;
  }
`;

const emailForNewsLetter = localStorage.getItem('emailForNewsLetter');

function AddToCartButton({
  cartQuantity,
  isPurchasable,
  productData,
  handleToggleSideCart,
  isWallpaper,
  rolls,
  sourceWallpaperSideBar,
  extendPlanSelected
}) {
  const history = useHistory();
  const [addToCart, { loading }] = useCreateCartProduct();
  const isMobile =
    typeof window !== `undefined` ? useMedia('(max-width: 992px) ') : null;
  const [
    isBundleProductsAddToCartLoading,
    setIsBundleProductsAddToCartLoading
  ] = useState(false);

  const isAuthenticated = useSelector(state => state.Auth.isAuthenticated);
  const bundleProducts = productData.bundle_products;
  const isBundle = bundleProducts?.length;
  const allowIndividualPurchases = productData?.allow_individual_purchases;
  const isBedroomSets =
    productData?.frame?.primarySubcategory.category_id == 39;

  const handleCart = () => {
    handleToggleSideCart({ sku: productData.sku, productData: productData });
  };

  const handleAddToCart = async () => {
    if (isBundle && allowIndividualPurchases) {
      setIsBundleProductsAddToCartLoading(true);

      if (isAuthenticated) {
        await Promise.all(
          [...bundleProducts].reverse().map(bp => {
            return addToCart({
              product: bp.product,
              quantity: bp.quantity,
              source: 20,
              extendPlan: extendPlanSelected,
              extendPlanId: extendPlanSelected
                ? extendPlanSelected.planId
                : null
            });
          })
        );
      } else {
        await Promise.all(
          [...bundleProducts].map(bp => {
            return addToCart({
              product: bp.product,
              quantity: bp.quantity,
              source: 20,
              extendPlan: extendPlanSelected,
              extendPlanId: extendPlanSelected
                ? extendPlanSelected.planId
                : null
            });
          })
        );
      }

      setIsBundleProductsAddToCartLoading(false);

      if (isMobile) {
        history.push(`/cart`);
      } else {
        handleToggleSideCart({ sku: bundleProducts[0]?.product.sku });
      }

      segmentTrackEvent(
        'Product Added',
        formatSegmentDataForProductAddedEvent(productData, cartQuantity)
      );
      return;
    }

    await addToCart({
      product: productData,
      quantity: cartQuantity,
      source: 3,
      extendPlan: extendPlanSelected,
      extendPlanId: extendPlanSelected ? extendPlanSelected.planId : null
    });
    handleCart();
  };

  const wallpaperText =
    rolls > 0
      ? `Add to Cart – ${rolls} Rolls for $${rolls * productData.price}`
      : 'Add to Cart';
  const addToCartText = sourceWallpaperSideBar
    ? wallpaperText
    : isBedroomSets
    ? 'Add Set to Cart'
    : 'Add to Cart';

  const getAddToCartButtonText = () => {
    if (!isPurchasable) {
      return 'Sold Out';
    }

    return addToCartText;
  }

  return (
    <Wrapper
      isWallpaper={isWallpaper || allowIndividualPurchases}
      disabled={
        loading ||
        !isPurchasable ||
        (sourceWallpaperSideBar && rolls < 1) ||
        isBundleProductsAddToCartLoading
      }
      onClick={handleAddToCart}
    >
      {getAddToCartButtonText()}
      {(loading || isBundleProductsAddToCartLoading) && (
        <div
          style={{ borderWidth: '2px', width: 15, height: 15, marginLeft: 20 }}
          className="spinner-border text-light"
          role="status"
        >
          <span className="sr-only">Loading...</span>
        </div>
      )}
    </Wrapper>
  );
}

export default AddToCartButton;

const NOTIFY_KLAVIYO = gql`
  mutation notifyKlaviyo($email: String!, $event: String!, $product_id: ID) {
    notifyKlaviyo(email: $email, event: $event, product_id: $product_id) {
      success
    }
  }
`;
